import { getErrorMessage } from "@captions-web/dev-logger";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { t } from "i18next";
import { toast } from "sonner";

import { BasicVideoPlayer } from "~/components/basic-video-player";
import { IntercomButton } from "~/components/intercom-button";
import { MirageLogoLarge } from "~/components/mirage-logo";
import { NoiseGrid } from "~/components/noise-grid";
import { Button, LinkButton } from "~/components/primitives/button";
import { TrackPageView } from "~/components/track-page-view";
import { SessionEvents } from "~/lib/analytics/defs/SessionEvents";
import { getAuthStore } from "~/modules/auth/auth-store";
import { getAuthRedirectUrlMutation } from "~/modules/auth/network";
import { getIntercomStore } from "~/stores/intercom";
import { css } from "~/styled-system/css";

export const Route = createFileRoute("/")({
  component: RouteComponent,
  async beforeLoad() {
    const isLikelyLoggedIn = getAuthStore().getIsLikelyLoggedIn();

    if (isLikelyLoggedIn) {
      throw redirect({ to: "/home" });
    }

    getIntercomStore().boot();
  },
});

function tryToGetStateFromUrl(url: string) {
  try {
    const urlObj = new URL(url);
    const state = urlObj.searchParams.get("state");
    return state ?? undefined;
  } catch (e) {
    return;
  }
}

function RouteComponent() {
  const { mutate, isPending, isSuccess } = getAuthRedirectUrlMutation.useMutation({
    onSuccess(data) {
      SessionEvents.generate_auth_url_status.track({
        status: "success",
        state: tryToGetStateFromUrl(data.url),
      });

      window.location.href = data.url;
    },
    onError(error) {
      SessionEvents.generate_auth_url_status.track({
        status: "fail",
        error_message: getErrorMessage(error),
      });

      toast.error(t("mirage-studio:landing-page.card.error-message"));
    },
  });

  const onClick = () => {
    mutate({ params: { callbackBaseUrl: window.location.origin } });
  };

  return (
    <div className={homeRootStyles}>
      <TrackPageView />
      <NoiseGrid />
      <div className={intercomContainerStyles}>
        <IntercomButton />
      </div>
      <div className={boxStyles}>
        <div className={landingLayoutStyles}>
          <div className={videoStyles}>
            <BasicVideoPlayer
              src="https://stream.mux.com/zYZv6kPGJv01qruRq6JNGVJDlzUcyPgG0000YWKaRPeKcI.m3u8"
              autoPlay
              loop
              muted
              playsInline
              controls={{ progress: true, muted: true }}
              theme={{
                fullWidth: true,
                fullHeight: true,
              }}
            />
          </div>
          <div className={landingContentStyles}>
            <div className={logoAndTitleStyles}>
              <div className={logoStyles}>
                <MirageLogoLarge />
              </div>
              <h2 className={css({ textStyle: "heading-1", color: "$gray.200" })}>
                {t("mirage-studio:title")}
              </h2>
              <p className={css({ textStyle: "body-1-subtle", color: "$gray.500" })}>
                {t("mirage-studio:landing-page.card.subtitle")}
              </p>
            </div>
            <div className={ctaAndTermsStyles}>
              <div className={interactiveElementStyles}>
                <Button
                  variant="primary"
                  size="lg"
                  // keep spinner spinning until we redirect user away from this page
                  isPending={isPending || isSuccess}
                  onClick={onClick}
                >
                  {t("mirage-studio:landing-page.card.get-started")}
                </Button>
                <LinkButton variant="secondary" size="lg" href="https://mirage.app/get-started">
                  {t("mirage-studio:landing-page.card.learn-more")}
                </LinkButton>
              </div>
              <p
                className={css({
                  textStyle: "body-4-subtle",
                  color: "$gray.500",
                  textWrap: "balance",
                })}
              >
                {t("mirage-studio:landing-page.card.terms-text")}{" "}
                <a
                  href="https://mirage.app/mirage-studio-terms"
                  className={css({
                    color: "$gray.200",
                    pointerEvents: "auto",
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mirage Studio Terms of Use
                </a>
                {t("mirage-studio:landing-page.card.terms-separator")}
                <a
                  href="https://www.captions.ai/legal/terms"
                  className={css({
                    color: "$gray.200",
                    pointerEvents: "auto",
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Captions Terms and Conditions
                </a>{" "}
                {t("mirage-studio:landing-page.card.terms-and")}{" "}
                <a
                  href="https://www.captions.ai/legal/privacy"
                  className={css({
                    color: "$gray.200",
                    pointerEvents: "auto",
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Captions Privacy Policy
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const homeRootStyles = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100dvh",
  backgroundColor: "$gray.950",
  overflow: "hidden",
  padding: "0 16px",
  "@media (min-width: 768px)": {
    padding: "0",
  },
});

const intercomContainerStyles = css({
  position: "fixed",
  right: "24px",
  bottom: "24px",
  zIndex: "$globalActions",
  pointerEvents: "auto",
});

const boxStyles = css({
  position: "relative",
  zIndex: 1,
  width: "100%",
  maxWidth: "700px",
  height: "552px",
  backgroundColor: "$surface.translucent",
  backdropFilter: "blur(2px)",
  borderRadius: "32px",
  pointerEvents: "none",
  boxShadow: "$highlight",
  "@media (min-width: 768px)": {
    width: "700px",
  },
  "@media (max-width: 768px)": {
    maxWidth: "400px",
  },
});

const interactiveElementStyles = css({
  position: "relative",
  zIndex: 2,
  pointerEvents: "auto",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  "& > button": {
    width: "100%",
  },
});

const landingLayoutStyles = css({
  display: "flex",
  alignItems: "stretch",
  gap: "32px",
  padding: "16px",
  height: "100%",
  "@media (max-width: 768px)": {
    justifyContent: "center",
  },
});

const landingContentStyles = css({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "60px 24px 16px",
  textAlign: "center",
  flex: "0 1 auto",
  height: "100%",
  maxWidth: "400px",
});

const logoAndTitleStyles = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
});

const ctaAndTermsStyles = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "16px",
});

const logoStyles = css({
  width: "60px",
  height: "60px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const videoStyles = css({
  width: "280px",
  aspectRatio: "9/16",
  borderRadius: "16px",
  flexShrink: 0,
  overflow: "hidden",
  pointerEvents: "auto",

  "@media (max-width: 768px)": {
    display: "none",
  },
});
